import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { logger } from "@amzn/afxtaco-utilities";
import { UploadedFileMeta } from "@amzn/afxtaco-types";
import { reducerType } from "../../redux/reducers";
import { listUploadedFiles } from "../api/assets/assets";

interface UseUploadedFilesOptions {
  projectId: string | undefined;
  /** Skip fetching (e.g. during the new-project flow before uploads exist). */
  skip?: boolean;
}

interface UseUploadedFilesResult {
  /** The files the user attached to this project, newest first. */
  files: UploadedFileMeta[];
  /**
   * Whether the list is being fetched for the first time. Stays false during a
   * refresh, so callers render the previous list rather than a skeleton.
   */
  isLoading: boolean;
}

/**
 * Fetches the files the user uploaded to a project.
 *
 * Uploads are excluded from both the additional-asset listing and the asset
 * manifest because they back no tab, so `GET /assets/uploads` is the only way to
 * enumerate them — and the in-chat file chips are in-memory only, so this is the
 * only surface that survives a reload.
 *
 * Re-fetches on two signals: `uploadsStaleCounter`, bumped when an upload batch
 * succeeds, and `assetsStaleCounter`, bumped on an asset-generating turn. The
 * first is what makes a just-attached file appear immediately; watching only the
 * asset counter left it invisible until the component happened to remount, which
 * in practice meant switching tabs and coming back.
 *
 * Refreshes are silent: `isLoading` reports only the first load, so an update
 * lands in place instead of collapsing the card back to a skeleton and flashing.
 *
 * Failures are logged and yield an empty list rather than an error state: a
 * missing upload list should never degrade the rest of the Overview.
 */
export const useUploadedFiles = ({
  projectId,
  skip = false,
}: UseUploadedFilesOptions): UseUploadedFilesResult => {
  const [files, setFiles] = useState<UploadedFileMeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const staleCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.assetsStaleCounter[projectId] ?? 0) : 0,
  );
  const uploadsCounter = useSelector((state: reducerType) =>
    projectId ? (state.Projects.uploadsStaleCounter[projectId] ?? 0) : 0,
  );

  // Whether a list has been shown for the current project, so a refresh can skip
  // the loading state. Reset alongside `files` when the project changes.
  const hasLoadedRef = useRef(false);

  const doFetch = useCallback(
    async (
      signal: AbortSignal,
      { silent = false }: { silent?: boolean } = {},
    ) => {
      if (!projectId || skip) return;
      // Stale-while-revalidate: only surface loading when there is nothing to
      // keep on screen.
      const showLoading = !silent || !hasLoadedRef.current;
      if (showLoading) setIsLoading(true);
      try {
        const response = await listUploadedFiles(projectId);
        if (signal.aborted) return;
        if (response?.success) {
          setFiles(response.data.files);
          hasLoadedRef.current = true;
        }
      } catch (error) {
        if (signal.aborted) return;
        logger.error({
          loggerMsg: "[useUploadedFiles] Failed to fetch uploaded files",
          projectId,
          error,
        });
      } finally {
        if (!signal.aborted && showLoading) setIsLoading(false);
      }
    },
    [projectId, skip],
  );

  // Fetch on mount / project change. Aborts in-flight requests on cleanup.
  useEffect(() => {
    if (!projectId || skip) {
      // Clear loading on the bail-out paths. A first load that is still in flight
      // when `skip` flips true (or the project clears) gets aborted by the
      // cleanup, and its `finally` deliberately does not touch loading, so
      // without this the card would show its skeleton forever.
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    doFetch(controller.signal);
    return () => controller.abort();
  }, [projectId, skip, doFetch]);

  // Re-fetch when an upload lands, or when a turn completes (which may have
  // added files by another route, e.g. the IDE/Kiro path).
  //
  // The baseline is tracked per project. Counters are read per project, so on a
  // switch the new project's values would otherwise be compared against the old
  // project's: a project uploaded to earlier in the session has a higher counter,
  // which reads as a bump and fires a second request on top of the mount fetch
  // below. Re-baselining on a switch makes that impossible.
  const prevCountersRef = useRef({ projectId, staleCounter, uploadsCounter });
  useEffect(() => {
    const prev = prevCountersRef.current;
    const sameProject = prev.projectId === projectId;
    prevCountersRef.current = { projectId, staleCounter, uploadsCounter };
    if (!sameProject || !projectId || skip) return;

    const bumped =
      staleCounter > prev.staleCounter || uploadsCounter > prev.uploadsCounter;
    if (!bumped) return;

    const controller = new AbortController();
    doFetch(controller.signal, { silent: true });
    return () => controller.abort();
  }, [staleCounter, uploadsCounter, projectId, skip, doFetch]);

  // Reset on project change so a stale list never bleeds across projects.
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      setFiles([]);
      hasLoadedRef.current = false;
      prevProjectIdRef.current = projectId;
    }
  }, [projectId]);

  return { files, isLoading };
};
